import { useState } from 'react';
import { useAppDataContext } from '../context/AppDataContext';
import { ShoppingBag, Check, Lock, Gem } from 'lucide-react';
import Critter from '../components/glyphs/Critter';

// --- קטלוג החנות המורחב (עם דרישות רמה) ---
const AVATAR_CATEGORIES = {
    robots: { title: 'רובוטים וחלל', items: [
        { id: 'avatar_robot', name: 'רובוט חכם', price: 50, minLevel: 1 },
        { id: 'avatar_astro', name: 'אסטרונאוט', price: 150, minLevel: 2 },
        { id: 'avatar_alien', name: 'חייזר', price: 200, minLevel: 3 },
        { id: 'avatar_rocket', name: 'חללית', price: 300, minLevel: 4 },
    ]},
    fantasy: { title: 'פנטזיה', items: [
        { id: 'avatar_ninja', name: 'נינג׳ה', price: 100, minLevel: 2 },
        { id: 'avatar_unicorn', name: 'חד קרן', price: 250, minLevel: 3 },
        { id: 'avatar_dragon', name: 'דרקון מעופף', price: 350, minLevel: 5 },
        { id: 'avatar_wizard', name: 'קוסם', price: 300, minLevel: 4 },
        { id: 'avatar_elf', name: 'אלף יער', price: 300, minLevel: 3 },
    ]},
    animals: { title: 'חיות חמודות', items: [
        { id: 'avatar_fox', name: 'שועל חכם', price: 100, minLevel: 1 },
        { id: 'avatar_lion', name: 'אריה אמיץ', price: 200, minLevel: 2 },
        { id: 'avatar_panda', name: 'פנדה רגוע', price: 150, minLevel: 1 },
        { id: 'avatar_cat', name: 'חתול קסם', price: 100, minLevel: 1 },
        { id: 'avatar_dog', name: 'כלב נאמן', price: 100, minLevel: 1 },
        { id: 'avatar_koala', name: 'קואלה', price: 150, minLevel: 2 },
    ]},
    specials: { title: 'מיוחדים (למתקדמים)', items: [
        { id: 'avatar_king', name: 'מלך', price: 1000, minLevel: 8 },
        { id: 'avatar_detective', name: 'בלש', price: 400, minLevel: 5 },
        { id: 'avatar_hero', name: 'גיבור על', price: 500, minLevel: 6 },
        { id: 'avatar_zombie', name: 'זומבי חמוד', price: 300, minLevel: 5 },
        { id: 'avatar_ghost', name: 'רוח רפאים', price: 300, minLevel: 5 },
    ]}
};

export default function Store() {
  const { profile, buyItem } = useAppDataContext();

  const [selectedItem, setSelectedItem] = useState<{id: string, name: string, price: number} | null>(null);

  const handleBuy = async () => {
      if (selectedItem) {
        await buyItem(selectedItem.id, selectedItem.price);
        setSelectedItem(null);
      }
  };

  return (
      <div className="flex flex-col h-full bg-background text-foreground p-4 dir-rtl overflow-y-auto custom-scrollbar relative">
      <div className="flex justify-between items-center bg-muted p-4 rounded-2xl border border-border shadow-xl">
        <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
            <ShoppingBag className="text-primary" />
            החנות
            </h1>
            <p className="text-muted-foreground text-sm">השתמש ביהלומים שצברת</p>
        </div>
        <div className="text-center bg-card px-4 py-2 rounded-xl border border-cyan-500/30 shadow-[0_0_15px_rgba(34,211,238,0.2)]">
            <span className="block text-xs text-muted-foreground">היתרה שלך</span>
            <span className="text-2xl font-bold text-primary flex items-center gap-1">
                {profile?.balance || 0} <Gem size={20} fill="currentColor" />
            </span>
        </div>
      </div>

      {/* רשימת קטגוריות ומוצרים */}
      <div className="pb-24 space-y-8 mt-6">
        {Object.entries(AVATAR_CATEGORIES).map(([key, category]) => (
            <div key={key}>
                <h2 className="text-lg font-bold text-muted-foreground mb-4 border-r-4 border-cyan-500 pr-3 flex items-center gap-2">
                    {category.title}
                </h2>
                
                <div className="grid grid-cols-3 gap-3">
                    {category.items.map((item) => {
                        const isOwned = profile?.owned_items?.includes(item.id);
                        const canAfford = (profile?.balance || 0) >= item.price;
                        const levelMet = (profile?.level || 1) >= item.minLevel;
                        const isLocked = !levelMet;

                        return (
                            <button 
                                key={item.id}
                                onClick={() => !isOwned && !isLocked && canAfford && setSelectedItem(item)}
                                disabled={isOwned || isLocked || !canAfford}
                                className={`relative p-3 rounded-2xl border flex flex-col items-center justify-between min-h-[150px] transition-all
                                    ${isOwned 
                                        ? 'bg-card/40 border-green-500/30 opacity-60' 
                                        : isLocked
                                            ? 'bg-card border-border opacity-50 grayscale cursor-not-allowed'
                                            : canAfford 
                                                ? 'bg-muted border-border hover:border-cyan-400 hover:bg-muted/80 hover:scale-105 shadow-lg cursor-pointer' 
                                                : 'bg-card border-border opacity-70'
                                    }`}
                            >
                                <div className="absolute top-2 left-2 text-[10px] font-bold text-muted-foreground bg-background px-1.5 rounded">
                                    רמה {item.minLevel}
                                </div>

                                <div className="mb-2 mt-4"><Critter id={item.id} size={56} /></div>
                                <span className="text-xs font-bold text-center leading-tight mb-2 text-foreground">{item.name}</span>
                                
                                {isOwned ? (
                                    <div className="mt-auto bg-green-500/20 text-green-400 text-[10px] font-bold px-2 py-1 rounded-full flex items-center gap-1 w-full justify-center">
                                        <Check size={10} /> נרכש
                                    </div>
                                ) : isLocked ? (
                                    <div className="mt-auto bg-muted text-muted-foreground text-[10px] font-bold px-2 py-1 rounded-full flex items-center gap-1 w-full justify-center border border-border">
                                       <Lock size={10} /> רמה {item.minLevel}
                                    </div>
                                ) : (
                                    <div className={`mt-auto text-[10px] font-bold px-2 py-1 rounded-full flex items-center justify-center gap-1 w-full ${canAfford ? 'bg-primary text-primary-foreground' : 'bg-red-900/40 text-red-300 border border-red-900'}`}>
                                        {item.price} <Gem size={10} fill="currentColor"/>
                                    </div>
                                )}
                            </button>
                        );
                    })}
                </div>
            </div>
        ))}
      </div>

      {/* Confirmation Modal */}
      {selectedItem && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 backdrop-blur-sm">
            <div className="bg-card border border-border rounded-2xl p-6 w-[85%] max-w-[320px] flex flex-col items-center text-center shadow-2xl transform transition-all scale-100">
                <h3 className="text-xl font-bold text-foreground mb-2">אישור קנייה</h3>
                <p className="text-muted-foreground text-sm mb-6">האם אתה בטוח שברצונך לרכוש פריט זה?</p>
                
                <div className="w-24 h-24 bg-bridge-soft rounded-full flex items-center justify-center mb-4 shadow-lg border-2 border-border">
                    <Critter id={selectedItem.id} size={72} />
                </div>
                
                <h4 className="text-lg font-bold text-primary mb-1">{selectedItem.name}</h4>
                <div className="flex items-center gap-1 text-primary font-bold mb-8 bg-muted px-3 py-1 rounded-full">
                    {selectedItem.price} <Gem size={16} fill="currentColor" />
                </div>

                <div className="flex gap-4 w-full">
                    <button 
                        onClick={() => setSelectedItem(null)}
                        className="flex-1 py-3 rounded-xl bg-muted text-muted-foreground font-bold hover:bg-muted transition-colors"
                    >
                        ביטול
                    </button>
                    <button 
                        onClick={handleBuy}
                        className="flex-1 py-3 rounded-xl bg-gradient-to-r from-primary to-accent text-primary-foreground font-bold hover:opacity-90 shadow-lg shadow-primary/20 transition-all hover:scale-105"
                    >
                        אשר קנייה
                    </button>
                </div>
            </div>
        </div>
      )}
    </div>
  );
}